import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Phone, MapPin, Linkedin } from "lucide-react";
import { useTranslation } from 'react-i18next';

const ContactSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
    serviceType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const serviceOptions = [
    { value: 'web-development', label: t('services.webDev.title') },
    { value: 'software-engineering', label: t('services.software.title') },
    { value: 'custom-keyboards', label: t('services.keyboards.title') },
    { value: '3d-printing', label: t('services.printing.title') },
    { value: 'pc-building', label: t('services.pcBuilding.title') },
    { value: 'home-automation', label: t('services.homeAutomation.title') },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('=== FORM SUBMIT TRIGGERED ===');
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log('Submitting form data:', formData);
      
      const { data, error } = await supabase.functions.invoke('contact-messages', {
        body: formData
      });

      console.log('Function response:', { data, error });

      if (error) throw error;

      if (data.success) {
        toast({
          title: t('contact.success'),
          description: t('contact.successDescription')
        });
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          message: '',
          serviceType: ''
        });
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: t('contact.error'),
        description: t('contact.errorDescription'),
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-6 relative">
      <div className="max-w-6xl mx-auto">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold mb-4 text-glow-cyber">
            <span className="bg-gradient-cyber bg-clip-text text-transparent">
              {t('contact.title')}
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Contact Form */}
          <Card className="glass p-8 border-glow hover-glow">
            <h3 className="text-2xl font-bold mb-6 text-glow-cyber">{t('contact.form.title')}</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input 
                  placeholder={t('contact.form.firstName')} 
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="glass border-surface-variant focus:border-cyber-blue"
                  required
                />
                <Input 
                  placeholder={t('contact.form.lastName')} 
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="glass border-surface-variant focus:border-cyber-blue"
                  required
                />
              </div>
              
              <Input 
                type="email" 
                placeholder={t('contact.form.email')} 
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="glass border-surface-variant focus:border-cyber-blue"
                required
              />
              
              <Input 
                placeholder={t('contact.form.subject')} 
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="glass border-surface-variant focus:border-cyber-blue"
                required
              />
              
              <Select 
                value={formData.serviceType} 
                onValueChange={(value) => handleInputChange('serviceType', value)}
              >
                <SelectTrigger className="glass border-surface-variant focus:border-cyber-blue" aria-label="Select service type">
                  <SelectValue placeholder="Select service type *" />
                </SelectTrigger>
                <SelectContent className="glass bg-background/95 backdrop-blur-md border-surface-variant z-50">
                  {serviceOptions.map((option) => (
                    <SelectItem 
                      key={option.value} 
                      value={option.value}
                      className="hover:bg-muted/50 focus:bg-muted/50 hover:text-foreground focus:text-foreground"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Textarea 
                placeholder={t('contact.form.message')} 
                rows={6}
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                className="glass border-surface-variant focus:border-cyber-blue resize-none"
                required
              />
              
              <Button 
                type="submit" 
                size="lg" 
                className="w-full bg-gradient-cyber text-primary-foreground hover-glow border-0 py-3"
                disabled={isSubmitting}
                onClick={(e) => {
                  console.log('=== BUTTON CLICKED ===');
                  console.log('Form data at click:', formData);
                }}
              >
                {isSubmitting ? t('contact.form.sending') : t('contact.form.send')}
              </Button>
            </form>
          </Card>

          {/* Contact Info */}
          <div className="space-y-8">
            
            {/* Contact Details */}
            <Card className="glass p-8 border-glow">
              <h3 className="text-2xl font-bold mb-6 text-glow-cyber">{t('contact.info.title')}</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-gradient-web glow-web">
                    <Mail className="w-5 h-5 text-background" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('contact.info.email')}</p>
                    <p className="text-foreground">juergen.belada@gmail.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-gradient-software glow-software">
                    <Phone className="w-5 h-5 text-background" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('contact.info.phone')}</p>
                    <p className="text-foreground">+43 660 1390949</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-gradient-keyboard glow-keyboard">
                    <MapPin className="w-5 h-5 text-background" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('contact.info.location')}</p>
                    <p className="text-foreground">Bad Sauerbrunn, Austria</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Social Links */}
            <Card className="glass p-8 border-glow">
              <h3 className="text-2xl font-bold mb-6 text-glow-cyber">{t('contact.social.title')}</h3>
              
              <div className="flex gap-4">
                <a 
                  href="https://www.linkedin.com/in/jürgen-belada-110a9a112"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit LinkedIn profile"
                >
                  <Button 
                    size="icon" 
                    variant="outline" 
                    className="glass border-cyber-green text-cyber-green hover:bg-cyber-green/10 hover-glow"
                  >
                    <Linkedin className="w-5 h-5" />
                  </Button>
                </a>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;